import { Component, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { TranslateModule } from '@ngx-translate/core';
import { AssistantType } from '@vendasta/ai-assistants';
import { v4 as uuidv4 } from 'uuid';
import { AiToolForm, AiToolFormArray } from '../../../core/forms';
import { AiToolFormRegistryService } from '../../../core/services/ai-tool-form-registry.service';
import {
  AiToolSelectDialogComponent,
  AiToolSelectDialogData,
  AiToolSelectDialogResult,
} from '../ai-tool-select-dialog/ai-tool-select-dialog.component';

@Component({
  selector: 'ai-tools-list',
  imports: [MatListModule, TranslateModule, MatButtonModule, MatIconModule],
  templateUrl: './ai-tools-list.component.html',
  styleUrl: './ai-tools-list.component.scss',
})
export class AiToolsListComponent {
  readonly forms = input.required<AiToolFormArray>();
  readonly showAddButton = input<boolean>(true);
  readonly toolFormDisplay = output<AiToolForm | undefined>();
  readonly assistantType = input<AssistantType>();
  // Need this input to avoid breaking change to v1 assistant-config form, can be removed after that form is deleted
  readonly allowRemovingAnyTools = input<boolean>(false);

  private readonly toolFormRegistry = inject(AiToolFormRegistryService);
  readonly addDialog = inject(MatDialog);

  handleToolRemoved(event: Event, idx: number) {
    event.stopPropagation();
    this.forms().removeAt(idx);
  }

  handleToolAdd(event: Event) {
    event.stopPropagation();

    const dialogRef = this.addDialog.open<
      AiToolSelectDialogComponent,
      AiToolSelectDialogData,
      AiToolSelectDialogResult
    >(AiToolSelectDialogComponent, {
      width: '1100px',
      height: '700px',
      data: {
        excludedFunctionIds: [
          ...this.forms()
            .controls.map((form) => form.controls.id.value)
            .filter((id) => id != null),
        ],
        assistantType: this.assistantType(),
      },
    });

    dialogRef.afterClosed().subscribe((result: AiToolSelectDialogResult) => {
      if (!result) {
        return;
      }

      const { selectedFunctions, newFunction } = result;

      if (selectedFunctions && selectedFunctions.length > 0) {
        selectedFunctions.forEach((selectedFunction) => {
          const toolId = selectedFunction.id;
          if (toolId) {
            const newToolForm = this.toolFormRegistry.getOrRegister(toolId, () => {
              return new AiToolForm({ func: selectedFunction });
            });
            this.forms().push(newToolForm);
          } else {
            const newToolForm = new AiToolForm({ func: selectedFunction });
            this.forms().push(newToolForm);
          }
        });
      } else if (newFunction) {
        // For new tools, create with a localId
        const newToolForm = new AiToolForm({ metadata: { isNew: true, localId: uuidv4() } });
        this.toolFormDisplay.emit(newToolForm);
      }
    });
  }
}
